import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { Phone, Mail, MapPin, CreditCard, RotateCcw, MessageCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_PHONE = "351910565133";
const EMAIL = "memoriasnaavenida@gmail.com";

export default function BookingForm() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    accommodation: "", arrival_date: "", departure_date: "",
    adults: 2, children: 0, message: "", consent: true,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    try {
      const pref = localStorage.getItem("vad_pref_acc");
      if (pref) setForm(f => ({ ...f, accommodation: pref }));
    } catch {}
  }, []);

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.accommodation || !form.arrival_date || !form.departure_date) {
      toast.error(t.booking.required);
      return;
    }
    if (new Date(form.departure_date) <= new Date(form.arrival_date)) {
      toast.error(lang === "pt" ? "Data de partida deve ser posterior à de chegada" : "Departure must be after arrival");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, language: lang });
      setDone(true);
      toast.success(t.booking.success_title);
      try { localStorage.removeItem("vad_pref_acc"); } catch {}
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || (lang === "pt" ? "Erro ao enviar pedido" : "Error sending request"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="booking-section" className="py-24 md:py-32 bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-16 max-w-2xl">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C48E66] mb-6" data-testid="booking-eyebrow">{t.booking.eyebrow}</p>
          <h2 className="font-serif text-5xl md:text-6xl text-[#112A46] font-light leading-[1.02] tracking-tight" data-testid="booking-title">
            {t.booking.title_1}<span className="italic">{t.booking.title_italic}</span>
          </h2>
          <p className="mt-6 text-[#5C665F] text-base md:text-lg leading-relaxed">{t.booking.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
          <div className="lg:col-span-5">
            <div className="space-y-8">
              <InfoItem icon={Phone} label={t.booking.whatsapp_label} value={`+${WA_PHONE.slice(0,3)} ${WA_PHONE.slice(3,6)} ${WA_PHONE.slice(6,9)} ${WA_PHONE.slice(9)}`}
                href={`tel:+${WA_PHONE}`} testid="info-phone" />
              <InfoItem icon={Mail} label={t.booking.email_label} value={EMAIL} href={`mailto:${EMAIL}`} testid="info-email" />
              <InfoItem icon={MapPin} label={t.booking.location_label} value={t.location.address} testid="info-address" />
              <InfoItem icon={CreditCard} label={t.booking.payment_label} value={t.booking.payment_value} testid="info-payment" />
              <InfoItem icon={RotateCcw} label={t.booking.cancel_label} value={t.booking.cancel_value} testid="info-cancel" />
            </div>

            <div className="mt-12 p-8 bg-[#112A46] text-[#FAFAF7]">
              <p className="font-serif italic text-2xl mb-4">{t.booking.or_wa}</p>
              <a
                href={`https://wa.me/${WA_PHONE}?text=${encodeURIComponent("Olá! Tenho interesse em reservar a Villa d'Avó Dalva.")}`}
                target="_blank" rel="noopener noreferrer"
                data-testid="whatsapp-section-btn"
                className="inline-flex items-center gap-3 bg-[#C48E66] text-[#112A46] px-6 py-3 text-[11px] uppercase tracking-[0.25em] font-medium hover:bg-[#FAFAF7] transition-all duration-300"
              >
                <MessageCircle size={16} strokeWidth={1.5} />
                {t.booking.wa_btn}
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="border border-[#E2DFD2] p-12 md:p-16 text-center bg-[#F0EBE1]"
                data-testid="booking-success"
              >
                <CheckCircle2 size={48} strokeWidth={1} className="text-[#112A46] mx-auto mb-6" />
                <h3 className="font-serif text-4xl text-[#112A46] font-light mb-4">{t.booking.success_title}</h3>
                <p className="text-[#5C665F] max-w-md mx-auto">{t.booking.success_sub}</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="border border-[#E2DFD2] bg-white p-8 md:p-12" data-testid="booking-form">
                <h3 className="font-serif text-3xl text-[#112A46] font-light mb-2">{t.booking.form_title}</h3>
                <p className="text-sm text-[#5C665F] mb-10">{t.booking.form_subtitle}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label={t.booking.first_name + " *"}>
                    <Input value={form.first_name} onChange={(e) => handleChange("first_name", e.target.value)}
                      data-testid="booking-first-name" className="border-0 border-b border-[#E2DFD2] rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-[#112A46]" required />
                  </Field>
                  <Field label={t.booking.last_name + " *"}>
                    <Input value={form.last_name} onChange={(e) => handleChange("last_name", e.target.value)}
                      data-testid="booking-last-name" className="border-0 border-b border-[#E2DFD2] rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-[#112A46]" required />
                  </Field>
                  <Field label={t.booking.email + " *"}>
                    <Input type="email" value={form.email} onChange={(e) => handleChange("email", e.target.value)}
                      data-testid="booking-email" className="border-0 border-b border-[#E2DFD2] rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-[#112A46]" required />
                  </Field>
                  <Field label={t.booking.phone}>
                    <Input value={form.phone} onChange={(e) => handleChange("phone", e.target.value)}
                      data-testid="booking-phone" className="border-0 border-b border-[#E2DFD2] rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-[#112A46]" />
                  </Field>

                  <div className="md:col-span-2">
                    <Field label={t.booking.accommodation + " *"}>
                      <Select value={form.accommodation} onValueChange={(v) => handleChange("accommodation", v)}>
                        <SelectTrigger data-testid="booking-accommodation" className="border-0 border-b border-[#E2DFD2] rounded-none px-0 shadow-none focus:ring-0">
                          <SelectValue placeholder={t.booking.select_option} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="v3" data-testid="opt-v3">{t.booking.v3_option}</SelectItem>
                          <SelectItem value="v1" data-testid="opt-v1">{t.booking.v1_option}</SelectItem>
                          <SelectItem value="both" data-testid="opt-both">{t.booking.both_option}</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <Field label={t.booking.arrival + " *"}>
                    <Input type="date" value={form.arrival_date} onChange={(e) => handleChange("arrival_date", e.target.value)}
                      data-testid="booking-arrival" className="border-0 border-b border-[#E2DFD2] rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-[#112A46]" required />
                  </Field>
                  <Field label={t.booking.departure + " *"}>
                    <Input type="date" value={form.departure_date} onChange={(e) => handleChange("departure_date", e.target.value)}
                      data-testid="booking-departure" className="border-0 border-b border-[#E2DFD2] rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-[#112A46]" required />
                  </Field>

                  <Field label={t.booking.adults + " *"}>
                    <Input type="number" min="1" max="10" value={form.adults} onChange={(e) => handleChange("adults", parseInt(e.target.value) || 1)}
                      data-testid="booking-adults" className="border-0 border-b border-[#E2DFD2] rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-[#112A46]" required />
                  </Field>
                  <Field label={t.booking.children + " *"}>
                    <Input type="number" min="0" max="10" value={form.children} onChange={(e) => handleChange("children", parseInt(e.target.value) || 0)}
                      data-testid="booking-children" className="border-0 border-b border-[#E2DFD2] rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-[#112A46]" required />
                  </Field>

                  <div className="md:col-span-2">
                    <Field label={t.booking.message}>
                      <Textarea rows={4} value={form.message} onChange={(e) => handleChange("message", e.target.value)}
                        data-testid="booking-message" className="border-0 border-b border-[#E2DFD2] rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-[#112A46] resize-none" />
                    </Field>
                  </div>
                </div>

                <label className="flex items-start gap-3 mt-8 text-xs text-[#5C665F] cursor-pointer">
                  <input type="checkbox" checked={form.consent} onChange={(e) => handleChange("consent", e.target.checked)}
                    data-testid="booking-consent" className="mt-0.5 accent-[#112A46]" />
                  <span>{t.booking.consent}</span>
                </label>

                <Button
                  type="submit"
                  disabled={loading || !form.consent}
                  data-testid="booking-submit-btn"
                  className="mt-10 w-full bg-[#112A46] hover:bg-[#C48E66] text-[#FAFAF7] rounded-none py-6 text-xs md:text-sm uppercase tracking-[0.3em] font-medium transition-all duration-300 disabled:opacity-50"
                >
                  {loading ? t.booking.sending : t.booking.submit}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#5C665F] font-medium">{label}</Label>
      {children}
    </div>
  );
}

function InfoItem({ icon: Icon, label, value, href, testid, multiline }) {
  const content = (
    <div className="flex items-start gap-4" data-testid={testid}>
      <Icon size={20} strokeWidth={1} className="text-[#C48E66] mt-1 flex-shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C665F] mb-1">{label}</div>
        <div className={`text-[#112A46] ${multiline ? "whitespace-pre-line" : ""} font-light`}>{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:text-[#C48E66] transition-colors">{content}</a> : content;
}
